import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { downloadExcel } from "../../finance/shared/excelService";
import { useToast } from "../../../shared/components/SharedUI";
import { useLang } from "../../../shared/i18n/useLang";
import { usePermisos } from "../../../shared/utils/permisos";
import {
  duracion, fechaHora, listarRegistros, texto as textoDe, textoMotivo,
} from "../services/seguridadService";

const PAGE_SIZE = 25;

function hoyISO(dias = 0) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const inp = {
  background: "var(--bg-input)", color: "var(--text-primary)", border: "1px solid var(--border-card)",
  borderRadius: 8, padding: "7px 10px", fontSize: "0.85rem",
};
const lbl = { fontSize: "0.75rem", color: "var(--text-secondary)" };
const th = { padding: "10px 12px", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.4,
  color: "var(--text-secondary)", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: "0.84rem", borderTop: "1px solid var(--border-card)", verticalAlign: "top" };
const btnMarca = { background: "var(--brand)", color: "#fff", border: "none", fontWeight: 600 };

/**
 * Bitácora de seguridad del condominio (administración): todos los ingresos y
 * salidas registrados por los guardas, con filtros y exportación a Excel.
 */
export default function SeguridadBitacora() {
  const { showToast, toastEl } = useToast();
  const L = useLang();
  const puede = usePermisos();
  const [filtros, setFiltros] = useState({ desde: hoyISO(-7), hasta: hoyISO(), q: "", tipo: "", adentro: false });
  const [page, setPage] = useState(1);
  const [datos, setDatos] = useState({ results: [], count: 0 });
  const [cargando, setCargando] = useState(true);

  const params = useCallback(() => ({
    desde: filtros.desde, hasta: filtros.hasta, q: filtros.q, tipo: filtros.tipo,
    adentro: filtros.adentro ? 1 : "",
  }), [filtros]);

  useEffect(() => {
    let cancel = false;
    setCargando(true);
    listarRegistros({ ...params(), page, page_size: PAGE_SIZE })
      .then((d) => !cancel && setDatos(d.results ? d : { results: d, count: d.length }))
      .catch((e) => !cancel && showToast(e.message, false))
      .finally(() => !cancel && setCargando(false));
    return () => { cancel = true; };
    // showToast cambia en cada render; no hace falta como dependencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, page]);

  function cambiar(campo, valor) {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPage(1);
  }

  async function exportar() {
    const u = new URLSearchParams(Object.entries(params()).filter(([, v]) => v !== "" && v != null));
    try {
      await downloadExcel(`/api/seguridad/registros/exportar-excel/?${u}`, "bitacora_seguridad.xlsx");
    } catch (e) {
      showToast(e.message, false);
    }
  }

  const paginas = Math.max(1, Math.ceil(datos.count / PAGE_SIZE));
  const COLUMNAS = [L("Entrada", "In"), L("Salida", "Out"), L("Tipo", "Type"), L("Persona", "Person"),
    L("Placa", "Plate"), L("Casa", "Unit"), L("Visita a", "Visiting"), L("Motivo", "Reason"), L("Registró", "Logged by")];

  return (
    <>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/" style={{ color: "inherit" }}>{L("Inicio", "Home")}</Link> /{" "}
        <strong style={{ color: "var(--text-primary)" }}>{L("Seguridad", "Security")}</strong>
      </span>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ margin: "6px 0 14px" }}>
        <div>
          <h2 className="mt-2 mb-1 p-0 title">{L("Bitácora de seguridad", "Security log")}</h2>
          <p className="m-0" style={{ color: "var(--text-muted)" }}>
            {L("Ingresos y salidas registrados por los guardas", "Entries and exits registered by the guards")}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {puede("seguridad_garita") && (
            <Link to="/garita" className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2">
              <i className="fa-solid fa-shield-halved" /> {L("Abrir app de Seguridad", "Open Security app")}
            </Link>
          )}
          <button type="button" className="btn btn-sm d-flex align-items-center gap-2" style={btnMarca} onClick={exportar}>
            <i className="fa-solid fa-file-excel" /> {L("Exportar Excel", "Export Excel")}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
        <label style={lbl}>{L("Desde", "From")}<br />
          <input type="date" style={inp} value={filtros.desde} onChange={(e) => cambiar("desde", e.target.value)} />
        </label>
        <label style={lbl}>{L("Hasta", "To")}<br />
          <input type="date" style={inp} value={filtros.hasta} onChange={(e) => cambiar("hasta", e.target.value)} />
        </label>
        <label style={{ ...lbl, flex: "1 1 220px" }}>{L("Buscar", "Search")}<br />
          <input style={{ ...inp, width: "100%" }}
            placeholder={L("Nombre, cédula, placa, casa o guarda", "Name, ID, plate, unit or guard")}
            value={filtros.q} onChange={(e) => cambiar("q", e.target.value)} />
        </label>
        <label style={lbl}>{L("Tipo", "Type")}<br />
          <select style={inp} value={filtros.tipo} onChange={(e) => cambiar("tipo", e.target.value)}>
            <option value="">{L("Todos", "All")}</option>
            <option value="VEHICULAR">{textoDe("VEHICULAR")}</option>
            <option value="PEATONAL">{textoDe("PEATONAL")}</option>
          </select>
        </label>
        <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "flex", gap: 6, alignItems: "center", paddingBottom: 8 }}>
          <input type="checkbox" checked={filtros.adentro} onChange={(e) => cambiar("adentro", e.target.checked)} />
          {L("Solo los que siguen adentro", "Only those still inside")}
        </label>
      </div>

      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{COLUMNAS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", color: "var(--text-secondary)" }}>{L("Cargando…", "Loading…")}</td></tr>
              ) : datos.results.length === 0 ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", color: "var(--text-secondary)" }}>
                  {L("No hay registros en este rango.", "No records in this range.")}
                </td></tr>
              ) : datos.results.map((r) => (
                <tr key={r.id}>
                  <td style={td}>{fechaHora(r.entrada)}</td>
                  <td style={td}>
                    {r.salida
                      ? <>{fechaHora(r.salida)}<div style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>{duracion(r.entrada, r.salida)}</div></>
                      : <span style={{ color: "var(--brand)", fontWeight: 700 }}>{L("Adentro", "Inside")}</span>}
                  </td>
                  <td style={td}>
                    <i className={`fa-solid ${r.tipo === "VEHICULAR" ? "fa-car-side" : "fa-person-walking"}`} /> {textoDe(r.tipo)}
                  </td>
                  <td style={td}>
                    <strong>{r.nombre}</strong>
                    <div style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>{r.identificacion} - {textoDe(r.categoria)}</div>
                    {r.notas && <div style={{ fontSize: "0.75rem", marginTop: 2 }}><i className="fa-solid fa-note-sticky" /> {r.notas}</div>}
                  </td>
                  <td style={td}>{r.placa || "-"}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.filial_nombre}</td>
                  <td style={td}>{r.persona_visitada || "-"}</td>
                  <td style={td}>{textoMotivo(r.motivo)}</td>
                  <td style={td}>
                    {r.registrado_por_nombre || "-"}
                    {r.salida_por_nombre && r.salida_por_nombre !== r.registrado_por_nombre && (
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>{L("Salida:", "Exit:")} {r.salida_por_nombre}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px",
          borderTop: "1px solid var(--border-card)", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
          <span>{L(`${datos.count} registro(s) - Página ${page} de ${paginas}`, `${datos.count} record(s) - Page ${page} of ${paginas}`)}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {L("Anterior", "Previous")}
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= paginas} onClick={() => setPage(page + 1)}>
              {L("Siguiente", "Next")}
            </button>
          </div>
        </div>
      </div>
      {toastEl}
    </>
  );
}
